/**
 * Helper: Attraqt Tracking
 * -----------------------------------------------------------------------------
 * - Universal component to interact with Attraqt
 * - Handles events using EventBus (e.g. `attraqt:trackEvent`)
 *
 * @namespace AttraqtTracking
 */
import * as xo from '@attraqt/xo-js'

/**
 * Create a new AttraqtTracking helper object.
 * @param {Boolean} checkout - Whether the object is initialised in Checkout
 * @param {Array} products - Array of checkout products from the Shopify.checkout object.
 */
export default ({ checkout = false, products = [] } = {}) => {

  /**
   * Global state.
   */
  const state = {
    allowEvents: false,
    attraqtReady: false,
  }

  /**
   * Initialise component.
   */
  function init() {
    setEventBusListeners()

    if (checkout) {
      setDefaultState()
    }
  }

  /**
   * Set the default state of the helper.
   */
  function setDefaultState() {
    state.allowEvents = (mint.customer.canBeTracked)

    /**
     * User does not consent - destroy.
     */
    if (!state.allowEvents) {
      return
    }

    initialiseAttraqt()
  }

  /**
   * Update state with sourceId which we get from the collection page.
   * @param {String} sourceId - response sourceId from `attraqt:sourceIdReceived` event.
   */
  function setSourceId(sourceId) {
    state.sourceId = sourceId
  }

  /**
   * Initialise Attraqt event tracking.
   */
  function initialiseAttraqt() {

    /**
     * Do not initialize tracking if it has already been initialized.
     */
    if (xo.activity.isInitialized || state.attraqtReady) {
      return
    }

    xo.init({
      activity: {
        trackerKey: window.mint.services.attraqt.tracking.key,
      },
    })

    /**
     * If we are on the collection or search page,
     * then we wait for the attraqt:sourceIdReceived event, to get source Id
     * and only then initialize
     * - Wrap in a setTimeout() as some `view` events have a race condition
     *   where the below event will fire *before* 'templates/product.js' have
     *   loaded and the event won't fire.
     */
    setTimeout(() => {
      if (
        mint.request.pageType !== 'search' &&
        mint.request.pageType !== 'collection'
      ) {
        state.attraqtReady = true
        mint.EventBus.dispatch('attraqt:ready')
      }
    })
  }

  /**
   * Set Event Bus Listeners
   */

  function setEventBusListeners() {

    /**
     * The user has modified their consent status.
     */
    mint.EventBus.on('customer:stateUpdated', () => {
      setDefaultState()
    })

    /**
     *  In case we are on a collection or search page
     *  Then we are waiting for the receive of sourceId
     *  Then we set it to the global state
     *  And initialize the attract tracking
     *  @param {String} payload.sourceId - response id from `attraqt:sourceIdReceived` event.
     */
    mint.EventBus.on('attraqt:sourceIdReceived', ({ sourceId }) => {
      if (state.attraqtReady) return

      setSourceId(sourceId)
      initialiseAttraqt()
      state.attraqtReady = true
      mint.EventBus.dispatch('attraqt:ready')
    })

    /**
     * A component has requested to send an event to Attraqt.
     */
    mint.EventBus.on('attraqt:trackEvent', (payload) => {
      trackEvent(payload)
    })

    /**
     * Listen to cart updates.
     */
    mint.EventBus.on('mint:cart:updated', (payload) => {
      if (payload.request?.preventTracking) {
        return
      }

      handleUpdatedCart(payload)
    })

    /**
     * Checkout purchase event.
     */
    if (checkout) {
      mint.EventBus.on('attraqt:ready', () => {
        handleCheckoutPurchase()
      })
    }
  }

  /**
   * Track cart updates.
   * @param {Object} payload - response object from `mint:cart:updated` event.
   */
  function handleUpdatedCart(payload) {
    const action = payload.request.event.split(':')[1]
    const {
      id,
      quantity,
    } = payload.request.data || payload.request

    const product = payload.items.find(item => {
      return item.variant_id === id
    })

    if (action === 'added') {
      const addToCartAction = {
        'action': 'add-to-cart',
        'target': {
          'product': `${product.product_id}`,
        },
        'metadata': {
          'quantity': quantity,
          'locale': 'en_GB',
        },
      }

      trackEvent(addToCartAction)
    }

    if (action === 'removed') {
      const removeFromCartAction = {
        'action': 'remove-from-cart',
        'target': {
          'product': `${id}`,
        },
      }

      trackEvent(removeFromCartAction)
    }
  }

  /**
   * Triggers if the component is being initiated in Checkout
   */
  function handleCheckoutPurchase() {
    if ( !products || !Shopify.checkout ) return

    products.forEach((item) => {
      const purchaseAction = {
        'action': 'purchase',
        'target': {
          'product': `${item.product_id}`,
        },
        'metadata': {
          'orderId': `${Shopify.checkout.order_id}`,
          'price': +item.price * 100,
          'quantity': item.quantity,
          'currency': `${Shopify.checkout.currency}`,
          'locale': 'en_GB',
        },
      }

      trackEvent(purchaseAction)
    })

  }

  /**
   * Send the event to Attraqt.
   * @param {Object} payload - Event data to submit to Attraqt.
   */
  function trackEvent(payload) {
    if (
      !xo?.activity?.isInitialized ||
      !state.attraqtReady ||
      !state.allowEvents
    ) {
      return
    }

    const data = state.sourceId ? { ...payload, 'sourceId': state.sourceId } : payload

    xo.activity.send(data)
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  })
}
